import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactMarkdown from 'react-markdown';
import {
  clearStaffSessions,
  getStaffSessionAsync,
  importStaffSessionFromJson,
  listStaffSessionsAsync,
} from '../../utils/sfStaffSession';
import { SF_PORTAL_VARIANTS, clearanceColor } from '../../data/sfAdvisorIntake';
import { packageIdFromSearchParams } from '../../utils/citizenAdvisorQueryPrefill';
import CitizenCivicClawPanel from '../../components/citizen/CitizenCivicClawPanel';

const CITY_OPTIONS = [
  { id: '', label: 'All cities' },
  { id: 'san-francisco', label: 'San Francisco' },
  { id: 'berkeley', label: 'Berkeley' },
  { id: 'san-jose', label: 'San José' },
  { id: 'demo-generic', label: 'Demo generic' },
];

function publicLnpHref(packet = {}, selected = {}) {
  if (packet.publicAppUrl) return packet.publicAppUrl;
  const code = packet.publicTrackingCode || selected.publicTrackingCode;
  if (!code) return null;
  return `https://interviewflow.studio/publicSector/lnp/public/applications/${encodeURIComponent(code)}`;
}

/**
 * Read-only planner verification of citizen advisor + intake sessions.
 */
export default function StudioV2PermitStaffPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cityFromUrl = packageIdFromSearchParams(searchParams) || '';
  const [cityFilter, setCityFilter] = useState(cityFromUrl);
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [importText, setImportText] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCityFilter(cityFromUrl);
  }, [cityFromUrl]);

  useEffect(() => {
    import('../../utils/discoverySession').then(({ trackDiscovery }) => {
      trackDiscovery('staff_opened', {
        packageId: cityFilter || 'all',
        portalId: 'staff',
      });
    }).catch(() => {});
  }, [cityFilter]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listStaffSessionsAsync({
        packageId: cityFilter || undefined,
      });
      setSessions(rows);
      if (selectedId) {
        const found = rows.find((s) => s.id === selectedId)
          || await getStaffSessionAsync(selectedId);
        setSelected(found || rows[0] || null);
      } else {
        setSelected(rows[0] || null);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedId, cityFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Hydrate assignedInspector / phase from live discovery session (staff packet may be stale).
  useEffect(() => {
    const sid = selected?.discoverySessionId || selected?.packet?.discoverySessionId;
    if (!sid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { getDiscoverySessionApi } = await import('../../api/applicationType.api');
        const data = await getDiscoverySessionApi(sid);
        const live = data?.session || data;
        if (cancelled || !live) return;
        setSelected((prev) => {
          if (!prev) return prev;
          const prevSid = prev.discoverySessionId || prev.packet?.discoverySessionId;
          if (prevSid !== sid) return prev;
          return {
            ...prev,
            meta: live.meta || prev.meta,
            phase: live.phase || prev.phase,
            lnpStatus: live.lnpStatus || prev.lnpStatus,
            publicTrackingCode: live.publicTrackingCode || prev.publicTrackingCode,
            packet: {
              ...(prev.packet || {}),
              meta: live.meta || prev.packet?.meta,
              phase: live.phase || prev.packet?.phase,
              lnpStatus: live.lnpStatus || prev.packet?.lnpStatus,
              publicTrackingCode: live.publicTrackingCode || prev.packet?.publicTrackingCode,
              intakeFields: live.intakeFields || prev.packet?.intakeFields,
            },
          };
        });
      } catch {
        /* offline / missing session — keep packet as-is */
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id, selected?.discoverySessionId, selected?.packet?.discoverySessionId]);

  const onCityChange = (next) => {
    setCityFilter(next);
    setSelectedId(null);
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set('city', next);
    else nextParams.delete('city');
    setSearchParams(nextParams, { replace: true });
  };

  const handleImport = () => {
    setMessage(null);
    try {
      const saved = importStaffSessionFromJson(importText);
      setImportText('');
      setSelectedId(saved.id);
      refresh();
      setMessage({ severity: 'success', text: `Imported session ${saved.id}` });
    } catch (e) {
      setMessage({ severity: 'error', text: e?.message || 'Import failed' });
    }
  };

  const handleClear = () => {
    clearStaffSessions();
    setSelectedId(null);
    setSelected(null);
    refresh();
    setMessage({ severity: 'info', text: 'Cleared local staff sessions (server packets remain until TTL)' });
  };

  const packet = selected?.packet || {};
  const lu = packet.landUse;
  const colors = lu?.status ? clearanceColor(lu.status) : null;
  const md = packet.summaryMarkdown
    || packet.projectSummary?.markdown
    || '_No summary markdown in this session. Import a project-summary JSON or download one from the citizen app._';
  const lnpHref = useMemo(
    () => publicLnpHref(packet, selected),
    [packet, selected],
  );
  const trackingCode = packet.publicTrackingCode || selected?.publicTrackingCode || null;
  const cityQs = cityFilter ? `?city=${encodeURIComponent(cityFilter)}` : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{
        px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper',
      }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="primary" sx={{ letterSpacing: 1.2 }}>
              Staff verification · read-only
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              Planner session review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review citizen advise and intake packets (parcel, routing, CLAW trail, LNP tracking).
              Filter by city; open the live publicSector application when a tracking code exists.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="staff-city-filter-label">City</InputLabel>
              <Select
                labelId="staff-city-filter-label"
                label="City"
                value={cityFilter}
                onChange={(e) => onCityChange(e.target.value)}
                data-testid="staff-city-filter"
              >
                {CITY_OPTIONS.map((c) => (
                  <MenuItem key={c.id || 'all'} value={c.id}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button component={RouterLink} to={`/v2/demo/permit${cityQs}`} size="small" variant="contained">
              Advisor
            </Button>
            <Button component={RouterLink} to={`/v2/demo/permit/intake${cityQs}`} size="small" variant="outlined">
              Intake
            </Button>
            {Object.values(SF_PORTAL_VARIANTS).map((v) => (
              <Button key={v.id} component={RouterLink} to={v.path} size="small" variant="text">
                {v.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ borderRadius: 0 }}>
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
          gap: 2,
          p: 2,
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              Sessions (
              {loading ? '…' : sessions.length}
              )
              {cityFilter ? ` · ${cityFilter}` : ''}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={refresh}>Refresh</Button>
              <Button size="small" color="warning" onClick={handleClear}>Clear local</Button>
            </Stack>
          </Stack>
          {!sessions.length && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No sessions
              {cityFilter ? ` for ${cityFilter}` : ''}
              . Run advisor download or create an LNP draft from intake.
            </Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>City / track</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => (
                <TableRow
                  key={s.id}
                  hover
                  selected={selected?.id === s.id}
                  onClick={() => {
                    setSelectedId(s.id);
                    setSelected(s);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                      {s.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {s.address || '—'}
                    </Typography>
                    {(s.publicTrackingCode || s.packet?.publicTrackingCode) && (
                      <Typography variant="caption" color="success.main" display="block" noWrap>
                        {s.publicTrackingCode || s.packet.publicTrackingCode}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {s.updatedAt ? new Date(s.updatedAt).toLocaleString() : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Chip size="small" variant="outlined" label={s.packageId || '—'} />
                      <Chip
                        size="small"
                        label={s.landUseStatus || s.primaryTrack || '—'}
                        sx={{
                          bgcolor: s.landUseStatus ? clearanceColor(s.landUseStatus).fill : undefined,
                          color: s.landUseStatus ? '#fff' : undefined,
                        }}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Import project-summary JSON
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            size="small"
            placeholder="Paste downloaded project-summary JSON"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
          />
          <Button
            sx={{ mt: 1 }}
            variant="outlined"
            size="small"
            disabled={!importText.trim()}
            onClick={handleImport}
          >
            Import
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          {!selected ? (
            <Typography color="text.secondary">Select a session to review.</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>{selected.title}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={`id: ${selected.id}`} variant="outlined" />
                  {selected.packageId && (
                    <Chip size="small" label={selected.packageId} color="primary" variant="outlined" />
                  )}
                  {selected.portalVariant && (
                    <Chip size="small" label={`portal: ${selected.portalVariant}`} />
                  )}
                  {selected.primaryTrack && (
                    <Chip size="small" color="primary" label={selected.primaryTrack} />
                  )}
                  {trackingCode && (
                    <Chip size="small" color="success" label={trackingCode} />
                  )}
                  {packet.claw?.mode && (
                    <Chip size="small" variant="outlined" label={`CLAW: ${packet.claw.mode}`} />
                  )}
                  {colors && (
                    <Chip
                      size="small"
                      label={lu.statusLabel || lu.status}
                      sx={{ bgcolor: colors.fill, color: '#fff', fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Box>

              {(selected.discoverySessionId || packet.discoverySessionId || trackingCode) && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {trackingCode ? (
                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        LNP tracking
                        {' '}
                        <strong>{trackingCode}</strong>
                      </Typography>
                      {lnpHref && (
                        <Button
                          component={Link}
                          href={lnpHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          variant="contained"
                          endIcon={<OpenInNewIcon />}
                          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                          data-testid="staff-open-lnp"
                        >
                          Open publicSector application
                        </Button>
                      )}
                    </Stack>
                  ) : 'Intake session on file'}
                  {(selected.discoverySessionId || packet.discoverySessionId) && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                      Discovery session:
                      {' '}
                      <code>{selected.discoverySessionId || packet.discoverySessionId}</code>
                      {' · '}
                      <Link
                        component={RouterLink}
                        to={`/v2/demo/permit/intake?session=${encodeURIComponent(selected.discoverySessionId || packet.discoverySessionId)}${cityFilter ? `&city=${encodeURIComponent(cityFilter)}` : ''}`}
                        underline="hover"
                      >
                        Open intake
                      </Link>
                    </Typography>
                  )}
                  {(() => {
                    const tools = [
                      ...(packet.meta?.claw?.toolsUsed || []),
                      ...(selected?.meta?.claw?.toolsUsed || []),
                      ...(packet.claw?.toolsUsed || []),
                    ].filter(Boolean);
                    const unique = [...new Set(tools)];
                    if (!unique.length) return null;
                    return (
                      <Typography
                        variant="caption"
                        display="block"
                        data-testid="staff-claw-tools-used"
                      >
                        CLAW tools:
                        {' '}
                        {unique.join(', ')}
                      </Typography>
                    );
                  })()}
                </Alert>
              )}

              {(selected.discoverySessionId || packet.discoverySessionId) && (
                <CitizenCivicClawPanel
                  mode="staff"
                  compact
                  sessionId={selected.discoverySessionId || packet.discoverySessionId}
                  packageId={selected.packageId || packet.packageId || cityFilter || null}
                  session={{
                    packageId: selected.packageId || packet.packageId,
                    applicationTypeId: packet.applicationTypeId || selected.applicationTypeId,
                    publicTrackingCode: trackingCode,
                    lnpStatus: packet.lnpStatus || selected.lnpStatus,
                    phase: packet.phase || selected.phase,
                    intakeFields: packet.intakeFields || packet.formPrefill,
                    formPrefill: packet.formPrefill,
                    routing: packet.routing,
                    parcel: packet.parcel,
                    meta: packet.meta || selected.meta || null,
                  }}
                  applicationStatus={packet.lnpStatus || selected.lnpStatus || null}
                />
              )}
              {(packet.meta?.assignedInspector || selected.meta?.assignedInspector) && (
                <Alert severity="success" sx={{ borderRadius: 2 }} data-testid="staff-assigned-inspector">
                  Assigned inspector:
                  {' '}
                  <strong>
                    {(packet.meta || selected.meta).assignedInspector.name
                      || (packet.meta || selected.meta).assignedInspector.inspectorId}
                  </strong>
                  {(packet.meta || selected.meta).assignedInspector.assignedAt
                    ? ` · ${(packet.meta || selected.meta).assignedInspector.assignedAt.slice(0, 10)}`
                    : ''}
                </Alert>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                }}
              >
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2">{selected.address || '—'}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Proposed use</Typography>
                  <Typography variant="body2">{selected.proposedUse || '—'}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">APN</Typography>
                  <Typography variant="body2">
                    {packet.parcel?.parcel?.mapblklot || packet.parcel?.parcel?.blklot || '—'}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Zoning</Typography>
                  <Typography variant="body2">
                    {packet.parcel?.zoning?.zoning
                      || packet.parcel?.zoning?.code
                      || packet.landUse?.zoningCode
                      || '—'}
                  </Typography>
                </Paper>
              </Box>

              {packet.fees?.total != null && (
                <Alert severity="warning">
                  Illustrative fee total:
                  {' '}
                  <strong>
                    $
                    {Number(packet.fees.total).toLocaleString()}
                  </strong>
                  {' '}
                  — not official City fees.
                </Alert>
              )}

              <Divider />
              <Typography variant="subtitle2">Citizen summary (read-only)</Typography>
              <Box sx={{
                '& h1,& h2,& h3': { mt: 1.5, mb: 0.5 },
                '& p': { mb: 1 },
                color: 'text.primary',
              }}
              >
                <ReactMarkdown>{md}</ReactMarkdown>
              </Box>

              <Alert severity="info">
                Guidance only — not an official City determination.
                {trackingCode
                  ? ' Use Open publicSector application to continue the filed draft.'
                  : ' Intake drafts hand off to publicSector when created from the citizen demo.'}
              </Alert>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
